const DAY_IN_MS = 24 * 60 * 60 * 1000;

class IwobblWelcomePopup extends HTMLElement {
  connectedCallback() {
    this.overlay = this.querySelector('[data-welcome-overlay]');
    this.dialog = this.querySelector('[data-welcome-dialog]');
    this.storageKey = this.dataset.storageKey || 'iwobbl_welcome_popup_v2';
    this.delayMs = Math.max(0, Number(this.dataset.delaySeconds || 4) * 1000);
    this.dismissMs = Math.max(1, Number(this.dataset.dismissDays || 30)) * DAY_IN_MS;
    this.designMode = this.dataset.designMode === 'true';

    this.handleClick = this.handleClick.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.handleVisibility = this.handleVisibility.bind(this);
    this.handleDiscountStatus = this.handleDiscountStatus.bind(this);

    this.addEventListener('click', this.handleClick);
    document.addEventListener('keydown', this.handleKeyDown);
    window.addEventListener('iwobbl:welcome-discount', this.handleDiscountStatus);

    this.initialize();
  }

  disconnectedCallback() {
    window.clearTimeout(this.openTimer);
    window.clearTimeout(this.closeTimer);
    document.removeEventListener('visibilitychange', this.handleVisibility);
    document.removeEventListener('keydown', this.handleKeyDown);
    this.removeEventListener('click', this.handleClick);
    window.removeEventListener('iwobbl:welcome-discount', this.handleDiscountStatus);

    if (this.hasAttribute('data-open')) this.restorePage();
  }

  initialize() {
    if (!this.overlay || !this.dialog) return;

    if (this.designMode) {
      this.open({ immediate: true });
      return;
    }

    if (!this.isEligible()) return;

    if (document.readyState === 'complete') {
      this.queueOpen();
    } else {
      window.addEventListener('load', () => this.queueOpen(), { once: true });
    }
  }

  isEligible() {
    if (this.readSession(`${this.storageKey}:shown`) === '1') return false;

    const dismissedUntil = Number(this.readLocal(`${this.storageKey}:dismissedUntil`) || 0);
    return !Number.isFinite(dismissedUntil) || dismissedUntil <= Date.now();
  }

  queueOpen() {
    if (document.hidden) {
      document.addEventListener('visibilitychange', this.handleVisibility, { once: true });
      return;
    }

    this.openTimer = window.setTimeout(() => this.open(), this.delayMs);
  }

  handleVisibility() {
    if (!document.hidden) this.queueOpen();
  }

  open({ immediate = false } = {}) {
    if (!immediate && !this.isEligible()) return;

    const anotherDialog = Array.from(
      document.querySelectorAll('[role="dialog"][aria-modal="true"]')
    ).find((candidate) => candidate !== this.dialog && !candidate.closest('[hidden]'));

    if (anotherDialog && !immediate) {
      this.retryCount = (this.retryCount || 0) + 1;
      if (this.retryCount <= 20) {
        this.openTimer = window.setTimeout(() => this.open(), 1000);
      }
      return;
    }

    this.writeSession(`${this.storageKey}:shown`, '1');
    this.showPopup();
  }

  handleClick(event) {
    const copyButton = event.target.closest('[data-welcome-copy]');
    if (copyButton) {
      event.preventDefault();
      void this.copyCode();
      return;
    }

    if (event.target.closest('[data-welcome-dismiss]')) {
      event.preventDefault();
      this.close();
    }
  }

  handleKeyDown(event) {
    if (event.key !== 'Escape' || !this.hasAttribute('data-open')) return;

    event.preventDefault();
    this.close();
  }

  showPopup() {
    if (this.hasAttribute('data-open')) return;

    this.previousActiveElement =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    this.previousScrollY = window.scrollY;
    this.previousBodyStyles = {
      width: document.body.style.width,
      position: document.body.style.position,
      top: document.body.style.top,
    };

    document.body.style.width = '100%';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${this.previousScrollY}px`;

    this.overlay.hidden = false;
    this.removeAttribute('data-closing');
    this.setAttribute('data-open', '');

    window.requestAnimationFrame(() => {
      this.querySelector('[data-welcome-initial-focus]')?.focus({ preventScroll: true });
    });
    this.emit('view');
  }

  close() {
    if (!this.hasAttribute('data-open') || this.hasAttribute('data-closing')) return;

    this.setAttribute('data-closing', '');
    window.clearTimeout(this.closeTimer);
    this.closeTimer = window.setTimeout(() => this.finishClose(), 180);
  }

  finishClose() {
    if (!this.hasAttribute('data-open')) return;

    this.overlay.hidden = true;
    this.removeAttribute('data-open');
    this.removeAttribute('data-closing');
    this.restorePage();
    this.writeLocal(`${this.storageKey}:dismissedUntil`, String(Date.now() + this.dismissMs));
    this.emit('dismiss');
  }

  restorePage() {
    const bodyStyles = this.previousBodyStyles;
    if (bodyStyles) {
      document.body.style.width = bodyStyles.width;
      document.body.style.position = bodyStyles.position;
      document.body.style.top = bodyStyles.top;
    }

    window.scrollTo({ top: this.previousScrollY || 0, behavior: 'instant' });
    this.previousActiveElement?.focus?.({ preventScroll: true });
    this.previousBodyStyles = null;
  }

  handleDiscountStatus(event) {
    const status = this.querySelector('[data-welcome-discount-status]');
    if (!status || event?.detail?.code !== 'WELCOME15') return;

    const messages = {
      pending: 'WELCOME15 will be added when you place an item in your bag.',
      verification: 'WELCOME15 is ready. Shopify will verify first-order eligibility at checkout.',
      applied: 'WELCOME15 is attached to your checkout.',
      ineligible: 'WELCOME15 is reserved for customers with no previous orders.',
      conflict: 'Another discount is active, so it has not been replaced.',
      failed: 'Automatic application is temporarily unavailable. You can copy WELCOME15 below.',
      removed: 'WELCOME15 was removed and will not be added again this session.',
      expired: 'Automatic application expired. You can copy WELCOME15 below.',
    };
    status.textContent = messages[event.detail.status] || '';
  }

  async copyCode() {
    const code = this.querySelector('[data-welcome-code]')?.textContent?.trim();
    if (!code) return;

    let copied = false;

    try {
      await navigator.clipboard.writeText(code);
      copied = true;
    } catch (_error) {
      const fallback = document.createElement('textarea');
      fallback.value = code;
      fallback.setAttribute('readonly', '');
      fallback.style.position = 'fixed';
      fallback.style.opacity = '0';
      document.body.appendChild(fallback);
      fallback.select();
      copied = document.execCommand('copy');
      fallback.remove();
    }

    const status = this.querySelector('[data-welcome-copy-status]');
    if (status) status.textContent = copied ? 'Code copied.' : 'Copy failed. Select the code above.';
    if (copied) this.emit('copy_code');
  }

  emit(name) {
    window.dispatchEvent(
      new CustomEvent('iwobbl:welcome-popup', {
        detail: {
          event: `welcome_popup_${name}`,
          popup: 'welcome_first_order_discount',
        },
      })
    );
  }

  readLocal(key) {
    try {
      return window.localStorage.getItem(key);
    } catch (_error) {
      return null;
    }
  }

  writeLocal(key, value) {
    try {
      window.localStorage.setItem(key, value);
    } catch (_error) {}
  }

  readSession(key) {
    try {
      return window.sessionStorage.getItem(key);
    } catch (_error) {
      return null;
    }
  }

  writeSession(key, value) {
    try {
      window.sessionStorage.setItem(key, value);
    } catch (_error) {}
  }
}

if (!customElements.get('iwobbl-welcome-popup')) {
  customElements.define('iwobbl-welcome-popup', IwobblWelcomePopup);
}
